import React, { useEffect, useMemo, useState } from 'react';

import {
    createAssemblyCloseRequest,
    createAssemblyReserveRequest,
    loadAssemblyMetadata,
} from './api';


const fallbackMetadata = {
    location: { city: 'Тюмень', address: 'Локальный пример' },
    today: '2026-05-20',
    systems: {
        '5X': [],
        '2X': [],
        'TE 2X': [],
    },
    counts: {
        '5X': 0,
        '2X': 0,
        'TE 2X': 0,
    },
};

const initialForm = {
    whom: '',
    related: '',
    application_sending: '',
    estimated_date_of_shipment: '',
    address: '',
    transport: '',
};


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function SummaryCard({ label, value }) {
    return (
        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}


function ModuleSelectorColumn({
    system,
    items,
    searchValue,
    selectedSerials,
    onSearchChange,
    onToggle,
}) {
    const normalizedQuery = searchValue.trim().toUpperCase();
    const selectedOrder = new Map(selectedSerials.map((serial, index) => [serial, index]));
    const selectedItems = items
        .filter((item) => selectedOrder.has(item.serial_number))
        .sort((left, right) => (
            (selectedOrder.get(left.serial_number) ?? Number.MAX_SAFE_INTEGER)
            - (selectedOrder.get(right.serial_number) ?? Number.MAX_SAFE_INTEGER)
        ));
    const filteredItems = normalizedQuery
        ? selectedItems.filter((item) => item.serial_number.includes(normalizedQuery))
        : selectedItems;

    return (
        <section className="shell-queueColumn">
            <div className="shell-queueColumnHeader">
                <h4>{system}</h4>
                <p>{selectedItems.length ? `${selectedItems.length} в заявке` : `${items.length} свободно для сканирования`}</p>
            </div>

            <label className="shell-field shell-field--selector">
                <span>Сканировать / фильтр</span>
                <input
                    type="text"
                    value={searchValue}
                    onChange={(event) => onSearchChange(system, event.target.value)}
                    placeholder="Серийный номер"
                />
            </label>

            <div className="shell-moduleSelectorList">
                {filteredItems.length ? (
                    filteredItems.map((item) => {
                        return (
                            <button
                                key={item.serial_number}
                                type="button"
                                className="shell-modulePick is-selected"
                                onClick={() => onToggle(item.serial_number)}
                            >
                                <div>
                                    <strong>{item.serial_number}</strong>
                                    <p>{item.party_number || '—'}</p>
                                </div>
                                <div className="shell-modulePickMeta">
                                    <span>{formatDate(item.arrival_date)}</span>
                                    <strong>В заявке</strong>
                                </div>
                            </button>
                        );
                    })
                ) : (
                    <div className="shell-emptyState">
                        {selectedItems.length ? 'Нет совпадений по этому фильтру.' : 'Отсканируй модуль, и он появится здесь.'}
                    </div>
                )}
            </div>
        </section>
    );
}


export default function AssemblingPage() {
    const [metadata, setMetadata] = useState(fallbackMetadata);
    const [warning, setWarning] = useState('');
    const [form, setForm] = useState(initialForm);
    const [selectedSerials, setSelectedSerials] = useState([]);
    const [searchMap, setSearchMap] = useState({
        '5X': '',
        '2X': '',
        'TE 2X': '',
    });
    const [submitState, setSubmitState] = useState({
        loading: false,
        action: '',
        error: '',
    });
    const [toast, setToast] = useState(null);

    async function bootstrap() {
        try {
            const payload = await loadAssemblyMetadata();
            setMetadata(payload);
            setForm((current) => ({
                ...current,
                application_sending: current.application_sending || payload.today || '',
            }));
            setWarning('');
        } catch (_error) {
            setMetadata(fallbackMetadata);
            setForm((current) => ({
                ...current,
                application_sending: current.application_sending || fallbackMetadata.today,
            }));
            setWarning('Не удалось загрузить доступные модули. Показан локальный пример.');
        }
    }

    useEffect(() => {
        bootstrap();
    }, []);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setToast(null);
        }, 2400);

        return () => window.clearTimeout(timer);
    }, [toast]);

    const groupedSystems = metadata.systems || fallbackMetadata.systems;
    const selectedCounters = useMemo(() => {
        const counters = { '5X': 0, '2X': 0, 'TE 2X': 0 };
        Object.entries(groupedSystems).forEach(([system, items]) => {
            counters[system] = items.filter((item) => selectedSerials.includes(item.serial_number)).length;
        });
        return counters;
    }, [groupedSystems, selectedSerials]);

    const summaryCards = [
        { label: 'Локация', value: metadata.location?.city || '—' },
        { label: 'Выбрано модулей', value: selectedSerials.length },
        { label: '5X / 2X / TE', value: `${selectedCounters['5X']} / ${selectedCounters['2X']} / ${selectedCounters['TE 2X']}` },
    ];

    function updateField(name, value) {
        setForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function updateSearch(system, value) {
        const normalized = value.trim().toUpperCase();
        if (!normalized) {
            setSearchMap((current) => ({
                ...current,
                [system]: value,
            }));
            return;
        }

        const exactMatch = Object.values(groupedSystems)
            .flat()
            .find((item) => item.serial_number.trim().toUpperCase() === normalized);

        if (exactMatch && !selectedSerials.includes(exactMatch.serial_number)) {
            setSelectedSerials((current) => [exactMatch.serial_number, ...current]);
            setSearchMap((current) => ({
                ...current,
                [system]: '',
            }));
            setToast({
                type: 'success',
                text: `Модуль ${exactMatch.serial_number} добавлен.`,
            });
            return;
        }

        setSearchMap((current) => ({
            ...current,
            [system]: value,
        }));
    }

    function toggleSerial(serialNumber) {
        setSelectedSerials((current) => (
            current.includes(serialNumber)
                ? current.filter((item) => item !== serialNumber)
                : [serialNumber, ...current]
        ));
    }

    async function submit(action) {
        setSubmitState({ loading: true, action, error: '' });

        const payload = {
            ...form,
            selected_serial_numbers: selectedSerials,
        };

        try {
            if (action === 'reserve') {
                await createAssemblyReserveRequest(payload);
            } else {
                await createAssemblyCloseRequest(payload);
            }

            setSelectedSerials([]);
            setSearchMap({ '5X': '', '2X': '', 'TE 2X': '' });
            setSubmitState({ loading: false, action: '', error: '' });
            setToast({
                type: 'success',
                text: action === 'reserve' ? 'Заявка сохранена в резерв.' : 'Заявка закрыта.',
            });
            await bootstrap();
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось сохранить заявку.');
            setSubmitState({
                loading: false,
                action: '',
                error: message,
            });
            setToast({
                type: 'error',
                text: message,
            });
        }
    }

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}

            <section className="shell-section shell-section--assemblyIntro">
                {warning ? <div className="shell-banner">{warning}</div> : null}
                {submitState.error ? <div className="shell-banner">{submitState.error}</div> : null}

                <div className="shell-grid shell-grid--main shell-grid--assembly shell-grid--assemblyTop">
                    <div className="shell-assemblyIntro">
                        <div className="shell-sectionHeader shell-sectionHeader--nested">
                            <div>
                                <h3>Сборка заявки</h3>
                            </div>
                        </div>

                        <form className="shell-searchForm shell-searchForm--compact shell-searchForm--assembly" onSubmit={(event) => event.preventDefault()}>
                            <label className="shell-field">
                                <span>Кому отправляем</span>
                                <input value={form.whom} onChange={(event) => updateField('whom', event.target.value)} />
                            </label>
                            <label className="shell-field">
                                <span>Куда</span>
                                <input value={form.related} onChange={(event) => updateField('related', event.target.value)} />
                            </label>
                            <label className="shell-field">
                                <span>Дата заявки</span>
                                <input
                                    type="date"
                                    value={form.application_sending}
                                    onChange={(event) => updateField('application_sending', event.target.value)}
                                />
                            </label>
                            <label className="shell-field">
                                <span>План отгрузки</span>
                                <input
                                    type="date"
                                    value={form.estimated_date_of_shipment}
                                    onChange={(event) => updateField('estimated_date_of_shipment', event.target.value)}
                                />
                            </label>
                            <label className="shell-field">
                                <span>Адрес получателя</span>
                                <input value={form.address} onChange={(event) => updateField('address', event.target.value)} />
                            </label>
                            <label className="shell-field">
                                <span>Транспортная</span>
                                <input value={form.transport} onChange={(event) => updateField('transport', event.target.value)} />
                            </label>
                        </form>
                    </div>
                </div>
            </section>

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Модули</span>
                        <h3>Готовые к сборке</h3>
                    </div>
                    <div className="shell-sectionHeaderActions">
                        <div className="shell-sectionToolbar">
                            <div className="shell-pageSummary shell-pageSummary--toolbar">
                                {summaryCards.map((item) => (
                                    <SummaryCard key={item.label} label={item.label} value={item.value} />
                                ))}
                            </div>
                            <div className="shell-actionCluster">
                                <button
                                    type="button"
                                    className="shell-actionButton shell-actionButton--secondary"
                                    onClick={() => submit('reserve')}
                                    disabled={submitState.loading}
                                >
                                    {submitState.loading && submitState.action === 'reserve' ? 'Сохраняем...' : 'Зарезервировать'}
                                </button>
                                <button
                                    type="button"
                                    className="shell-actionButton shell-actionButton--primary"
                                    onClick={() => submit('close')}
                                    disabled={submitState.loading}
                                >
                                    {submitState.loading && submitState.action === 'close' ? 'Сохраняем...' : 'Закрыть заявку'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="shell-queueColumns shell-queueColumns--modulePicker">
                    {Object.entries(groupedSystems).map(([system, items]) => (
                        <ModuleSelectorColumn
                            key={system}
                            system={system}
                            items={items}
                            searchValue={searchMap[system]}
                            selectedSerials={selectedSerials}
                            onSearchChange={updateSearch}
                            onToggle={toggleSerial}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}
